import { z } from 'zod';
import { DashboardTableStatusTone } from '@shigo-ui/dashboard-ui';

import { dashboardT } from '../../../lib/messages';
import { numberFormat } from '../../../lib/format';

export enum PublisherStatus {
  Active = 'ACTIVE',
  Archived = 'ARCHIVED',
}

export enum PublisherSortProperty {
  Name = 'name',
  NetworkName = 'networkName',
  SecondaryId = 'secondaryId',
  Status = 'status',
  CreatedOn = 'createdOn',
}

export enum PublisherSortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

export enum PublisherCostType {
  Cpm = 'CPM',
  RevenueShare = 'REVSHARE',
  Placement = 'PLACEMENT',
}

export enum PublisherCpmType {
  AdRequests = 'AD_REQUESTS',
  FilledImpressions = 'FILLED_IMPRESSIONS',
  AdImpressions = 'AD_IMPRESSIONS',
}

export enum PublisherDetailsTab {
  Overview = 'overview',
  Apps = 'apps',
  Domains = 'domains',
  Placements = 'placements',
  AppRequests = 'app-requests',
  A2v = 'a2v',
}

export enum PublisherXpParameter {
  Xp1 = 'xp1',
  Xp2 = 'xp2',
  Xp3 = 'xp3',
  Xp4 = 'xp4',
  Xp5 = 'xp5',
  Xp6 = 'xp6',
  Xp7 = 'xp7',
  Xp8 = 'xp8',
  Xp9 = 'xp9',
  Xp10 = 'xp10',
}

const nullableString = z.string().nullish();
const nullableBoolean = z.boolean().nullish();
const publisherCostSchema = z
  .looseObject({
    costType: z.enum(PublisherCostType),
    value: z.number().nullable(),
    cpmMetric: z.enum(PublisherCpmType).nullish(),
  })
  .nullable()
  .optional();

export const publisherSchema = z.looseObject({
  id: z.union([z.string(), z.number()]).transform(String),
  name: z.string(),
  status: z.enum(PublisherStatus),
  appsCnt: z.number().nullish(),
  domainsCnt: z.number().nullish(),
  placementsIds: z.array(z.string()).nullish(),
  createdOn: nullableString,
  updatedOn: nullableString,
  createdBy: z
    .union([z.string(), z.looseObject({ email: nullableString, username: nullableString })])
    .nullish(),
  secondaryId: nullableString,
  network: z.looseObject({ name: nullableString }).nullish(),
  notes: nullableString,
  companyName: nullableString,
  contactName: nullableString,
  phone: nullableString,
  email: nullableString,
  websiteUrl: nullableString,
  address: nullableString,
  city: nullableString,
  state: nullableString,
  zipCode: nullableString,
  country: nullableString,
  schainAsi: nullableString,
  schainSid: nullableString,
  rewritePublisherId: nullableBoolean,
  alternativePublisherId: nullableString,
  gamIdEnabled: nullableBoolean,
  gamAdUnitId: nullableString,
  mcmActive: nullableBoolean,
  debug: nullableBoolean,
  whitelistAppsEnabled: nullableBoolean,
  whitelistDomainsEnabled: nullableBoolean,
  revenueShareIo: z.number().nullish(),
  allProductsCost: publisherCostSchema,
  saasCost: publisherCostSchema,
  xpParametersMappings: z
    .array(z.object({ value: z.string(), xpParameter: z.enum(PublisherXpParameter) }))
    .nullish(),
});

export const publishersPageSchema = z.looseObject({
  content: z.array(publisherSchema),
  totalElements: z.number(),
});

export type Publisher = z.infer<typeof publisherSchema>;

export type PublisherListFilter = {
  searchTerm: string;
  statuses: PublisherStatus[];
  page: number;
  size: number;
  sortProperty: PublisherSortProperty | null;
  sortDirection: PublisherSortDirection;
};

export type PublisherCostPayload = {
  costType: PublisherCostType;
  value: number | null;
  cpmMetric?: PublisherCpmType | null;
};

export type PublisherMutationPayload = {
  name: string;
  status: PublisherStatus;
  notes: string;
  companyName: string;
  contactName: string;
  phone: string;
  email: string;
  websiteUrl: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  schainAsi: string;
  schainSid: string;
  rewritePublisherId: boolean;
  alternativePublisherId: string;
  gamIdEnabled: boolean;
  gamAdUnitId: string;
  mcmActive: boolean;
  whitelistAppsEnabled: boolean;
  whitelistDomainsEnabled: boolean;
  revenueShareIo: number | null;
  allProductsCost: PublisherCostPayload | null;
  saasCost: PublisherCostPayload | null;
  xpParametersMappings: Array<{ value: string; xpParameter: PublisherXpParameter }>;
};

export const publisherPageSizes = [5, 10, 20, 50, 100] as const;
export const defaultPublisherListFilter: PublisherListFilter = {
  searchTerm: '',
  statuses: [PublisherStatus.Active],
  page: 0,
  size: 20,
  sortProperty: PublisherSortProperty.Name,
  sortDirection: PublisherSortDirection.Asc,
};

export function serializePublisherListFilter(filter: PublisherListFilter) {
  const params = new URLSearchParams({
    page: String(filter.page),
    size: String(filter.size),
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
  });
  if (filter.searchTerm.trim()) params.set('name', filter.searchTerm.trim());
  if (filter.statuses.length) params.set('status', filter.statuses.join(','));
  return params;
}

export function isPublisherSortProperty(value: string): value is PublisherSortProperty {
  return Object.values(PublisherSortProperty).includes(value as PublisherSortProperty);
}

export function isPublisherSortDirection(value: string): value is PublisherSortDirection {
  return Object.values(PublisherSortDirection).includes(value as PublisherSortDirection);
}

export function resolvePublisherStatusLabel(status: PublisherStatus) {
  return dashboardT(
    status === PublisherStatus.Active ? 'publishers.status.active' : 'publishers.status.archived',
  );
}

export function resolvePublisherStatusTone(status: PublisherStatus) {
  return status === PublisherStatus.Active
    ? DashboardTableStatusTone.Success
    : DashboardTableStatusTone.Neutral;
}

export function resolvePublisherCreatedBy(publisher: Publisher) {
  if (typeof publisher.createdBy === 'string') return publisher.createdBy;
  return publisher.createdBy?.email ?? publisher.createdBy?.username ?? '-';
}

export function formatPublisherCost(cost: Publisher['allProductsCost']) {
  if (!cost) return '-';
  if (cost.costType === PublisherCostType.Placement) {
    return dashboardT('publisherDetails.cost.placement');
  }
  if (cost.value === null) return '-';
  if (cost.costType === PublisherCostType.Cpm) {
    return `$${numberFormat().format(cost.value)}`;
  }
  return `${numberFormat().format(cost.value * 100)}%`;
}
